/*
 * _customled-relay-finder.js  (Maranello M1.10.x)
 *
 * Search + filter logic for the motorcycle LED flasher relay compatibility
 * finder (snippets/_customled-relay-finder.liquid). Vanilla JS, no jQuery
 * dependency. Self-initializes on DOMContentLoaded; no-op if the wrapper
 * element isn't on the page.
 *
 * Behavior:
 *   - text search across model + OEM PN + make (with year smarts)
 *   - relay-product filter dropdown
 *   - make filter dropdown
 *   - one-at-a-time toggle for relay-note-popover details elements
 *
 * Lightbox: the Activ8 build used jQuery.magnificPopup. Maranello has no
 * jQuery — replaced with Maranello's native <modal-dialog> web component
 * (defined in assets/scripts.js, extends HTMLElement). A single hidden
 * modal-dialog is created on first click and reused for all 29 guidance
 * links. Inherits Maranello's Escape key, backdrop click, focus trap,
 * and body scroll-lock behavior. Consistent with how Maranello opens
 * product-image popups.
 *
 * Page-gated load via _customled-stylesheets.liquid (only on
 * /pages/motorcycle-led-flasher-relay-compatibility).
 */
(function () {
  "use strict";

  function init() {
    var finder = document.getElementById('relay-compatibility-finder');
    if (!finder) return;

    var searchInput = document.getElementById('relay-search-input');
    var relayFilter = document.getElementById('relay-product-filter');
    var makeFilter = document.getElementById('relay-make-filter');
    var clearButton = document.getElementById('relay-clear-filters');
    var visibleCount = document.getElementById('relay-visible-count');
    var noResults = document.getElementById('relay-no-results');
    var rows = Array.prototype.slice.call(finder.querySelectorAll('.relay-result-row'));
    var groups = Array.prototype.slice.call(finder.querySelectorAll('.relay-make-group'));

    function normalizeWords(value) {
      return String(value || '')
        .toLowerCase()
        .replace(/&/g, ' and ')
        .replace(/[^a-z0-9]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    }
    function normalizeCompact(value) { return normalizeWords(value).replace(/\s+/g, ''); }
    function getQueryYears(rawValue) {
      var matches = String(rawValue || '').match(/\b(?:19|20)\d{2}\b/g) || [];
      var unique = [];
      matches.forEach(function (year) { if (unique.indexOf(year) === -1) unique.push(year); });
      return unique;
    }
    function getTextQuery(rawValue) { return normalizeWords(String(rawValue || '').replace(/\b(?:19|20)\d{2}\b/g, ' ')); }
    function rowMatchesYears(row, queryYears) {
      if (!queryYears.length) return true;
      var rowYears = ' ' + String(row.getAttribute('data-years') || '').replace(/\s+/g, ' ') + ' ';
      if (!rowYears.trim()) return false;
      return queryYears.every(function (year) { return rowYears.indexOf(' ' + year + ' ') !== -1; });
    }
    function rowHasToken(row, token) {
      var tokenCompact = normalizeCompact(token);
      if (!tokenCompact) return true;

      // Very short model-code searches (R1/R6/H2/VTX) match the START of a
      // make/model word, so a partial code like VTX finds VTX1300. Kept to the
      // make/model text so OEM model-code suffixes (VN1500-R1) and relay names
      // (ELFR-1-QD) don't false-match.
      if (tokenCompact.length <= 3) {
        return (' ' + row._relayModelWords).indexOf(' ' + tokenCompact) !== -1;
      }

      if (row._relaySearchTokenMap[tokenCompact]) return true;
      return row._relaySearchCompact.indexOf(tokenCompact) !== -1;
    }
    function rowMatchesText(row, textQuery) {
      if (!textQuery) return true;
      var rowSearchWords = row._relaySearchWords;
      var rowSearchCompact = row._relaySearchCompact;
      var queryCompact = normalizeCompact(textQuery);
      if (!queryCompact) return true;

      if (queryCompact.length > 3 && rowSearchWords.indexOf(textQuery) !== -1) return true;
      // Compact phrase matching handles VSTAR/V Star, GSXR/GSX-R, YZFR1/YZF-R1, etc.
      if (queryCompact.length > 3 && rowSearchCompact.indexOf(queryCompact) !== -1) return true;

      var queryTokens = textQuery.split(' ').filter(function (token) { return token; });
      if (queryTokens.length && queryTokens.every(function (token) { return rowHasToken(row, token); })) return true;

      if (queryTokens.length > 1 && queryTokens.every(function (token) { return token.length > 1; })) {
        var reversedQueryCompact = queryTokens.slice().reverse().join('');
        if (reversedQueryCompact.length > 3 && rowSearchCompact.indexOf(reversedQueryCompact) !== -1) return true;
      }
      return false;
    }

    rows.forEach(function (row) {
      var rowSearch = row.getAttribute('data-search');
      var modelCell = row.querySelector('.relay-model');
      var modelSearch = [row.getAttribute('data-make') || '', modelCell ? modelCell.textContent : ''].join(' ');

      row._relaySearchWords = normalizeWords(rowSearch);
      row._relaySearchCompact = normalizeCompact(rowSearch);
      row._relaySearchTokenMap = {};
      row._relaySearchWords.split(' ').forEach(function (token) {
        if (token) row._relaySearchTokenMap[token] = true;
      });

      row._relayModelWords = normalizeWords(modelSearch);
      row._relayModelCompact = normalizeCompact(modelSearch);
      row._relayModelTokenMap = {};
      row._relayModelWords.split(' ').forEach(function (token) {
        if (token) row._relayModelTokenMap[token] = true;
      });
    });

    function applyFilters() {
      var rawQuery = searchInput.value;
      var textQuery = getTextQuery(rawQuery);
      var queryYears = getQueryYears(rawQuery);
      var relay = relayFilter.value;
      var make = makeFilter.value;
      var shown = 0;
      rows.forEach(function (row) {
        var rowRelay = row.getAttribute('data-relay');
        var rowMake = row.getAttribute('data-make');
        var textMatch = rowMatchesText(row, textQuery);
        var yearMatch = rowMatchesYears(row, queryYears);
        var relayMatch = !relay || rowRelay === relay;
        var makeMatch = !make || rowMake === make;
        var isVisible = textMatch && yearMatch && relayMatch && makeMatch;
        row.classList.toggle('relay-hidden', !isVisible);
        if (isVisible) shown += 1;
      });
      groups.forEach(function (group) {
        var visibleRows = group.querySelectorAll('.relay-result-row:not(.relay-hidden)').length;
        group.classList.toggle('relay-hidden', visibleRows === 0);
        if (rawQuery || relay || make) { group.open = visibleRows > 0; }
      });
      if (visibleCount) visibleCount.textContent = shown;
      if (noResults) noResults.hidden = shown !== 0;
    }

    searchInput.addEventListener('input', applyFilters);
    relayFilter.addEventListener('change', applyFilters);
    makeFilter.addEventListener('change', applyFilters);
    clearButton.addEventListener('click', function () {
      searchInput.value = '';
      relayFilter.value = '';
      makeFilter.value = '';
      applyFilters();
      searchInput.focus();
    });

    applyFilters();

    // One-at-a-time UX for relay-note-popover details elements:
    // when one opens, close any others that are open.
    document.addEventListener('toggle', function (event) {
      var target = event.target;
      if (!target || !target.classList || !target.classList.contains('relay-note-popover')) return;
      if (!target.open) return;
      var others = document.querySelectorAll('.relay-note-popover[open]');
      others.forEach(function (other) {
        if (other !== target) other.open = false;
      });
    }, true);

    // --- Install-guidance lightbox via Maranello's <modal-dialog> ---
    // Reuses the vendor web component for consistency with how Maranello
    // opens product-image popups. Single modal element, image src
    // updated per click. Escape, backdrop click, focus trap, body
    // scroll-lock all inherited from ModalDialog (assets/scripts.js).
    var LIGHTBOX_LINK_PATTERN = /ELFR-1-QD_Self-Cancel_Relay_Hookup|Replacement_Flasher_Relay_Connected/;
    var MODAL_ID = 'customled-guidance-modal';

    function getOrCreateGuidanceModal() {
      var existing = document.getElementById(MODAL_ID);
      if (existing) return existing;
      var tmp = document.createElement('div');
      tmp.innerHTML =
        '<modal-dialog id="' + MODAL_ID + '" class="media-modal customled-guidance-modal">' +
          '<div role="dialog" aria-label="Installation guidance" tabindex="-1">' +
            '<button id="ModalClose-' + MODAL_ID + '" type="button" class="customled-guidance-modal__close" aria-label="Close">' +
              '<svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true" focusable="false">' +
                '<path d="M4 4l12 12M16 4L4 16" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round"/>' +
              '</svg>' +
            '</button>' +
            '<img class="customled-guidance-modal__img" src="" alt="">' +
          '</div>' +
        '</modal-dialog>';
      var modal = tmp.firstChild;
      document.body.appendChild(modal);
      // ModalDialog's constructor wires up close button + Escape; nothing more to do
      return modal;
    }

    finder.addEventListener('click', function (event) {
      var link = event.target.closest('a[href]');
      if (!link) return;
      if (!LIGHTBOX_LINK_PATTERN.test(link.href)) return;
      event.preventDefault();
      var modal = getOrCreateGuidanceModal();
      modal.querySelector('.customled-guidance-modal__img').src = link.href;
      if (typeof modal.show === 'function') {
        modal.show(link);
      } else {
        // Defensive: ModalDialog hasn't upgraded yet — wait one frame
        requestAnimationFrame(function () {
          if (typeof modal.show === 'function') modal.show(link);
        });
      }
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
